import { useUpdatePreferences } from "@/hooks/useUpdatePreferences";
import { createPageOverride } from "@/lib/page-override";
import { readBrowserPreferences, resolvePreferences } from "@/lib/preferences";
import { useOptionalAuthSession } from "@/lib/session-context";

/** A read-only session's hide, shared by every checklist and guidance card. */
const useReadOnlyHidden = createPageOverride<boolean>();

/**
 * Whether on-page setup guidance — the setup checklists on client and deal
 * pages, and the guidance card pinned in Maxwell — is hidden. A server-side
 * preference (`session.preferences.show_guidance`), so hiding it from one
 * checklist hides it everywhere, on every browser. Default is visible.
 *
 * A read-only session (a super admin viewing as someone) refuses writes, so
 * there a hide lasts only for the page — still on every surface at once. Outside the shell there is no session
 * and nothing to hide: guidance shows and the setter does nothing.
 */
export function useGuidanceVisibility(): [boolean, (hidden: boolean) => void] {
  const session = useOptionalAuthSession();
  const update = useUpdatePreferences();
  const [localHidden, setLocalHidden] = useReadOnlyHidden(session?.user_id);

  if (!session) return [false, () => {}];

  const saved = !resolvePreferences(session, readBrowserPreferences(session.user_id)).show_guidance;
  if (session.impersonating) return [localHidden ?? saved, setLocalHidden];
  return [saved, (hidden) => update.mutate({ show_guidance: !hidden })];
}
